import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { subscribeStreamInputs } from '@/api/notifications';
import { streamInputsAtom } from '@/state/notificationsBridgeAtoms';
import type { Unsubscribe } from '@/types/common';
import type { StreamInput } from '@/types/notifications';
import type { Store } from '@/types/store';

type StreamSource = (onInput: (input: StreamInput) => void) => Unsubscribe;

/**
 * Feeds the notifications stream into `store`'s atoms for as long as the calling component is
 * mounted. `subscribe` is the live stream unless a test hands in its own.
 *
 * The stream hands over each frame on its own, and a reconnect replays the status of every
 * camera. The inputs that arrive together go to the store in one write, after the browser has
 * delivered all the frames it holds: a replay then costs one pass over what depends on every
 * camera, not one pass for each camera.
 */
export function useNotificationsBridge(
    store: Store, subscribe: StreamSource = subscribeStreamInputs,
): void {
    const send = useSetAtom(streamInputsAtom, { store });
    useEffect(() => {
        let pendingStreamInputs: StreamInput[] = [];
        let flushTimer: ReturnType<typeof setTimeout> | undefined;
        const flush = (): void => {
            flushTimer = undefined;
            const inputs = pendingStreamInputs;
            pendingStreamInputs = [];
            send(inputs);
        };
        const unsubscribe = subscribe((input): void => {
            pendingStreamInputs.push(input);
            flushTimer ??= setTimeout(flush, 0);
        });
        return (): void => {
            // What is still pending belongs to the store this bridge leaves.
            clearTimeout(flushTimer);
            unsubscribe();
        };
    }, [subscribe, send]);
}
